import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const TYPE_LABEL = {
  offer_letter: 'Offer Letter',
  experience_letter: 'Experience Letter',
  internship_letter: 'Internship Letter',
  payslip: 'Payslip',
};

const TYPE_COLOR = {
  offer_letter: 'bg-blue-50 text-blue-700 border-blue-200',
  experience_letter: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  internship_letter: 'bg-purple-50 text-purple-700 border-purple-200',
  payslip: 'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * Employee-facing document portal. Anyone logged in sees ONLY their own
 * documents (backend enforces the filter when role !== super_admin).
 */
export default function MyDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/hr/documents')
      .then(res => setDocs(res.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const download = async (doc) => {
    try {
      const res = await api.get(`/hr/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${doc.title || doc.doc_type}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="p-6 min-h-full bg-slate-50" data-testid="my-documents-page">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="h-6 w-6 text-[#5B6FD6]" /> My Documents
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Your offer letter, experience letter, internship letter and monthly payslips, all in one place.
        </p>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>
      ) : docs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <FileCheck className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No documents yet. Your HR will push them here when generated.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map(d => (
            <Card key={d.id} className="hover:shadow-md transition-shadow" data-testid={`my-doc-${d.id}`}>
              <CardContent className="p-4">
                <div className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border mb-2 ${TYPE_COLOR[d.doc_type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {TYPE_LABEL[d.doc_type] || d.doc_type}
                </div>
                <p className="text-sm font-semibold text-slate-800 mb-1 line-clamp-1">{d.title}</p>
                <p className="text-xs text-slate-500 mb-3">
                  {new Date(d.created_at).toLocaleString()}
                </p>
                <Button
                  size="sm"
                  onClick={() => download(d)}
                  className="w-full bg-[#5B6FD6] hover:bg-[#4338CA]"
                  data-testid={`download-doc-${d.id}`}
                >
                  <Download className="h-4 w-4 mr-1.5" /> Download
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
